import { Modal, Form, Input, Button, message } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, BankOutlined, LockOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import useAuthStore from '@store/useAuthStore';
import api from '@/api/api';
import { t } from '@/utils/translator';

function UserProfileModal({ open, onClose }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const user = useAuthStore((state) => state.user);
  const fetchMe = useAuthStore((state) => state.fetchMe);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        user_phonenumber: user.user_phonenumber,
        company_name: user.company_name,
        user_position: user.user_position,
      });
    }
  }, [open, user, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const updateData = {
        username: values.username,
        user_phonenumber: values.user_phonenumber,
        company_name: values.company_name,
        user_position: values.user_position,
      };

      // 비밀번호가 입력된 경우에만 추가
      if (values.new_password) {
        updateData.new_password = values.new_password;
      }

      await api.put('/users/' + user.user_id, updateData);
      message.success(t('개인정보가 성공적으로 수정되었습니다.'));
      await fetchMe();
      onClose();
    } catch (error) {
      console.error('Profile update error:', error);
      message.error(t('개인정보 수정에 실패했습니다.'));

      const errorDetail = error.response.data?.detail;
          
      // 비밀번호 관련 에러 처리
      const passwordErrors = {
        'PASSWORD_MINIMUM_LENGTH_8_REQUIRED': '비밀번호는 최소 8자리 이상이어야 합니다.',
        'PASSWORD_AT_LEAST_2_CHARACTER_TYPES_REQUIRED': '숫자, 영문, 특수문자 중 최소 2개이상을 조합해 주세요.',
        'PASSWORD_2_CHARACTER_TYPES_REQUIRE_MINIMUM_LENGTH_10': '숫자, 영문, 특수문자 중 최소 2가지 이상 그리고 10자리 이상의 비밀번호여야 합니다.',
        'INVALID_PASSWORD': '유효하지 않은 비밀번호 입니다.',
        'PASSWORD_REQUIRE_2_TYPES_MIN_10_OR_3_TYPES_MIN_8': '숫자, 영문, 특수문자 중 최소 2가지 이상에 10자리 또는 숫자, 영문, 특수문자 중 모두 사용한 최소 8자리의 비밀번호를 사용해주세요.',
        'ALREADY_REGISTERED': '이미 등록된 사용자입니다.',
        'INVALID_EMAIL': '유효하지 않은 이메일 주소입니다.',
        'INVALID_VENDOR_CODE': '유효하지 않은 벤더코드입니다.',
        'USER_NOT_FOUND': '사용자를 찾을 수 없습니다.',
        'COMPANY_NOT_FOUND': '회사 정보를 찾을 수 없습니다.',
        'PAST_USED_PASSWORD': '이전에 사용된 비밀번호는 재사용할 수 없습니다.'
      };
      
      const errorMessage = passwordErrors[errorDetail] || data.detail || '입력 정보를 확인해주세요.';
      message.error(t(errorMessage));
      

    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowPasswordFields(false);
    onClose();
  };

  return (
    <Modal
      title={t('개인정보 수정')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '20px' }}
      >
        <Form.Item
          label={t('이름')}
          name="username"
          rules={[
            { required: true, message: '이름을 입력해주세요.' },
            { min: 2, message: '이름은 최소 2자 이상이어야 합니다.' },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="이름을 입력하세요"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label={t('이메일')}
          name="email"
          rules={[
            { required: true, message: '이메일을 입력해주세요.' },
            { type: 'email', message: '올바른 이메일 형식을 입력해주세요.' },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="이메일을 입력하세요"
            size="large"
            disabled
          />
        </Form.Item>

        <Form.Item
          label={t('전화번호')}
          name="user_phonenumber"
          rules={[
            { required: true, message: '전화번호를 입력해주세요.' },
            // {
            //   pattern: /^[0-9-]+$/,
            //   message: '올바른 전화번호 형식을 입력해주세요.',
            // },
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="전화번호를 입력하세요"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label={t('회사명')}
          name="company_name"
          rules={[
            { required: true, message: '회사명을 입력해주세요.' },
            { min: 2, message: '회사명은 최소 2자 이상이어야 합니다.' },
          ]}
        >
          <Input
            prefix={<BankOutlined />}
            placeholder="회사명을 입력하세요"
            size="large"
            disabled
          />
        </Form.Item>


        <Form.Item
          label={t('직책')}
          name="user_position"
          rules={[
            { required: true, message: '직책을 입력해주세요.' },
            { min: 2, message: '직책은 최소 2자 이상이어야 합니다.' },
          ]}
        >
          <Input
            prefix={<BankOutlined />}
            placeholder="직책을 입력하세요"
            size="large"
          />
        </Form.Item>

        <Form.Item label={t('비밀번호 변경')}>
          <Button
            type={showPasswordFields ? "default" : "primary"}
            onClick={() => {
              setShowPasswordFields(!showPasswordFields);
              if (showPasswordFields) {
                // 비밀번호 필드 숨길 때 해당 필드들 초기화
                form.setFieldsValue({
                  new_password: undefined,
                  confirm_password: undefined
                });
              }
            }}
            size="large"
          >
            {showPasswordFields ? t('비밀번호 변경 취소') : t('비밀번호 변경')}
          </Button>
        </Form.Item>

        {showPasswordFields && (
          <>
            <Form.Item
              label={t('새 비밀번호')}
              name="new_password"
              rules={[
                { required: true, message: '새 비밀번호를 입력해주세요.' },
                { min: 8, message: '비밀번호는 최소 8자 이상이어야 합니다.' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="새 비밀번호를 입력하세요"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('새 비밀번호 확인')}
              name="confirm_password"
              dependencies={['new_password']}
              rules={[
                { required: true, message: t('새 비밀번호 확인을 입력해주세요.') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const newPassword = getFieldValue('new_password');
                    
                    // 둘 다 입력되었는데 일치하지 않는 경우
                    if (value && newPassword !== value) {
                      return Promise.reject(new Error(t('비밀번호가 일치하지 않습니다.')));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="새 비밀번호를 다시 입력하세요"
                size="large"
              />
            </Form.Item>
          </>
        )}



        <Form.Item style={{ marginBottom: 0, marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button onClick={handleCancel} size="large">
              {t('취소')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
            >
              {t('수정')}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default UserProfileModal;